/**
 * `TextLayer` → ag-psd `LayerTextData`. The baked bitmap is what displays; this is a best-effort
 * editing affordance — Photoshop re-renders on edit against a name-matched font. The box slack,
 * baseline sources and engine-data landmines are all explained in this directory's AGENTS.md.
 */

import { layerScale } from "@hightouch/lib/ad-studio/html-editor/layer-css";
import type {
  Justification,
  LayerTextData,
  TextStyle,
  TextStyleRun,
} from "ag-psd";

import {
  type TextAlign,
  type TextLayer,
  textHugsWidth,
} from "@hightouch/lib/ad-studio/html-editor/model";
import { parseFontFamilyName } from "@hightouch/lib/ad-studio/html-editor/font-family";
import {
  type SegmentStyle,
  segmentsFromContent,
  type TextSegment,
} from "@hightouch/lib/ad-studio/html-editor/text-segments";

import {
  type Affine,
  applyPoint,
  boxTransform,
  multiply,
} from "@hightouch/lib/ad-studio/psd-export/affine";
import { parseCssLength } from "@hightouch/lib/ad-studio/psd-export/css-length";
import { parseCssColorAsRgba } from "@hightouch/lib/ad-studio/psd-export/psd-color";
import {
  denotesBoldFace,
  denotesItalicFace,
  type PsdFontResolver,
} from "@hightouch/lib/ad-studio/psd-export/psd-font";
import { resolveTransformOrigin } from "@hightouch/lib/ad-studio/psd-export/transform-origin";
import {
  FALLBACK_FONT_METRICS,
  type PsdFontMetrics,
  type PsdFontMetricsResolver,
} from "@hightouch/lib/ad-studio/psd-export/psd-font-metrics";

/** CSS weights ≥ 600 are rendered bold; below that Photoshop's regular face. */
const FAUX_BOLD_MIN_WEIGHT = 600;

/** Photoshop's auto-leading multiple. Not CSS `line-height: normal` — see `cssLineHeightOf`. */
const PS_AUTO_LEADING_RATIO = 1.2;

/** ~3% extra on the PSD text box so a Photoshop re-render doesn't wrap a line the browser didn't. */
export const TEXT_BOX_WIDTH_BUFFER = 1.03;

/** Extra lines of box height, for the same re-wrap as {@link TEXT_BOX_WIDTH_BUFFER}. */
const BOX_HEIGHT_BUFFER_AMOUNT = 2;

/** PSD all-caps: a display flag, so the authored case survives an edit. (1 is small caps.) */
const FONT_CAPS_ALL = 2;

const JUSTIFICATION_BY_TEXT_ALIGN: Record<TextAlign, Justification> = {
  left: "left",
  center: "center",
  right: "right",
  justify: "justify-left",
};

/** Segments with line breaks (`<br>` and block children) lowered to `\n`. */
export function textSegmentsOf(layer: TextLayer): TextSegment[] {
  const base: SegmentStyle = {
    color: layer.color,
    fontWeight: layer.fontWeight,
    italic: layer.italic,
    underline: layer.underline,
    fontSize: layer.fontSize,
  };
  return segmentsFromContent({ content: layer.content, base });
}

/** The segments' plain text, line breaks included as `\n`. */
export function plainTextOfSegments(segments: TextSegment[]): string {
  return segments.map((segment) => segment.text).join("");
}

/**
 * `width`/`height` must be the RENDERED box — `text-measure.ts` fills the hugged axes in first.
 *
 * `parentTransform` plus the layer's own rotation and flips ride in the PSD `Trnf` matrix, which
 * Photoshop re-lays the text out through on edit — anything missing here is lost then.
 */
export function buildTextLayerData(
  layer: TextLayer,
  parentTransform: Affine,
  options: {
    /** Absent → the CSS family goes through verbatim. */
    resolveFont?: PsdFontResolver;
    /** Absent → {@link FALLBACK_FONT_METRICS} for every face. */
    measureFont?: PsdFontMetricsResolver;
    /** Rendered first baseline. Absent → derived from font metrics, see {@link firstBaselineOffset}. */
    measuredBaseline?: number;
    /** The layer's segments, when the caller already has them. Absent → re-derived here. */
    segments?: TextSegment[];
  } = {},
): LayerTextData {
  const { resolveFont, measureFont } = options;
  const metrics = fontMetricsOf(layer, measureFont);
  const segments = options.segments ?? textSegmentsOf(layer);
  const text = plainTextOfSegments(segments);

  const layerFont = psdFontFor({
    fontFamily: layer.fontFamily,
    fontWeight: layer.fontWeight,
    italic: layer.italic,
    resolveFont,
  });
  const style: TextStyle = {
    font: { name: layerFont.name },
    fontSize: layer.fontSize,
    fillColor: parseCssColorAsRgba(layer.color),
    underline: layer.underline,
    fauxBold: layerFont.fauxBold,
    fauxItalic: layerFont.fauxItalic,
  };
  const fontCaps = fontCapsOf(layer);
  if (fontCaps !== undefined) style.fontCaps = fontCaps;
  const tracking = trackingFromLetterSpacing(
    layer.letterSpacing,
    layer.fontSize,
  );
  if (tracking !== null) style.tracking = tracking;
  const leading = leadingFromLineHeight(layer.lineHeight, layer.fontSize);
  if (leading !== null) {
    style.leading = leading;
    style.autoLeading = false; // Photoshop honors ag-psd's default `autoLeading` flag over the number.
  } else {
    style.autoLeading = true;
  }

  const styleRuns: TextStyleRun[] = segments.map((segment) => {
    const segmentFont = psdFontFor({
      fontFamily: layer.fontFamily,
      fontWeight: segment.fontWeight,
      italic: segment.italic,
      resolveFont,
    });
    const runStyle: TextStyle = {
      fillColor: parseCssColorAsRgba(segment.color),
      fauxBold: segmentFont.fauxBold,
      fauxItalic: segmentFont.fauxItalic,
      underline: segment.underline,
    };
    if (segmentFont.name !== layerFont.name) {
      runStyle.font = { name: segmentFont.name };
    }
    // The base style already carries the layer's size; a run only needs its override. Leading stays
    // the layer's -- a run-level `line-height` is not carried through {@link TextSegment} yet.
    if (segment.fontSize !== undefined && segment.fontSize !== layer.fontSize) {
      runStyle.fontSize = segment.fontSize;
    }
    return { length: segment.text.length, style: runStyle };
  });

  // A hugged axis is width 0, which collapses on re-render; point text is Photoshop's hug.
  const isPointText = textHugsWidth(layer) || layer.width <= 0;
  const width = isPointText ? 0 : layer.width * TEXT_BOX_WIDTH_BUFFER;
  const height = isPointText ? 0 : boxHeightOf(layer, text);

  const justification = JUSTIFICATION_BY_TEXT_ALIGN[layer.textAlign];
  const firstBaseline =
    options.measuredBaseline ?? firstBaselineOffset(layer, metrics);
  // The type origin in the layer's box coordinates. Point text hangs off its alignment point's
  // baseline; box text off the frame's top-left, raised because Photoshop drops a full leading
  // below the box top where the browser lands at `firstBaseline`.
  const origin = isPointText
    ? { x: pointAnchorX(layer, justification), y: firstBaseline }
    : {
        x: -boxLeftOverhang(justification, width - layer.width),
        y: firstBaseline - effectiveLeading(layer),
      };
  const matrix = textLayerTransform(layer, parentTransform);
  const placed = applyPoint(matrix, origin);

  const data: LayerTextData = {
    text,
    transform: [matrix[0], matrix[1], matrix[2], matrix[3], placed.x, placed.y],
    style,
    // ag-psd's engine data defaults `autoHyphenate` on; CSS never hyphenates without `hyphens: auto`.
    paragraphStyle: { justification, autoHyphenate: false },
    // ag-psd writes these four unconditionally; undefined would write NaN.
    left: placed.x,
    top: placed.y,
    right: placed.x + width,
    bottom: placed.y + height,
  };
  if (isPointText) {
    data.shapeType = "point";
  } else {
    data.shapeType = "box";
    data.boxBounds = [0, 0, width, height];
  }
  if (styleRuns.length > 0) data.styleRuns = styleRuns;
  return data;
}

/**
 * `text-transform` as engine-data caps. Only `uppercase` has a counterpart; `lowercase` and
 * `capitalize` revert to the authored case on edit, which beats guessing at the browser's word
 * segmentation.
 */
function fontCapsOf(layer: TextLayer): number | undefined {
  const transform = layer.extraStyles?.["text-transform"]?.trim().toLowerCase();
  return transform === "uppercase" ? FONT_CAPS_ALL : undefined;
}

/** The leading Photoshop lays out with. Box height and baseline offset must share it. */
function effectiveLeading(layer: TextLayer): number {
  return (
    leadingFromLineHeight(layer.lineHeight, layer.fontSize) ??
    layer.fontSize * PS_AUTO_LEADING_RATIO
  );
}

/** Measured height, or line count for a hugged box; {@link BOX_HEIGHT_BUFFER_AMOUNT} added either way. */
function boxHeightOf(layer: TextLayer, text: string): number {
  const lineHeight = effectiveLeading(layer);
  const measured =
    layer.height > 0
      ? layer.height
      : Math.max(1, text.split("\n").length) * lineHeight;
  // Rounded up: a box a hundredth of a pixel short still clips a line.
  return Math.ceil(measured + lineHeight * BOX_HEIGHT_BUFFER_AMOUNT);
}

/**
 * The browser's first baseline, down from the layer's top edge: CSS centers the font's content box
 * in the line box, so half the leftover leading sits above the ascent. The analytic fallback for
 * layers `text-measure.ts` can't reach — see AGENTS.md, baseline side-channel.
 */
export function firstBaselineOffset(
  layer: TextLayer,
  metrics: PsdFontMetrics,
): number {
  const contentHeight = (metrics.ascent + metrics.descent) * layer.fontSize;
  const halfLeading = (cssLineHeightOf(layer, metrics) - contentHeight) / 2;
  return halfLeading + metrics.ascent * layer.fontSize;
}

/** The browser's line box: resolved `line-height`, or the face's ascent + descent for `normal`. */
function cssLineHeightOf(layer: TextLayer, metrics: PsdFontMetrics): number {
  return (
    leadingFromLineHeight(layer.lineHeight, layer.fontSize) ??
    (metrics.ascent + metrics.descent) * layer.fontSize
  );
}

function fontMetricsOf(
  layer: TextLayer,
  measureFont: PsdFontMetricsResolver | undefined,
): PsdFontMetrics {
  const measured = measureFont?.({
    fontFamily: layer.fontFamily,
    fontWeight: layer.fontWeight,
    italic: layer.italic,
  });
  return measured ?? FALLBACK_FONT_METRICS;
}

/**
 * How much of {@link TEXT_BOX_WIDTH_BUFFER}'s extra width sits LEFT of the layer's own box.
 * Photoshop justifies against the widened frame, so hanging all of it off the right edge slides
 * centered text half the buffer right and right-aligned text the whole of it.
 */
function boxLeftOverhang(justification: Justification, extra: number): number {
  if (justification === "center") return extra / 2;
  if (justification === "right") return extra;
  return 0;
}

/**
 * The type origin across the hugged box. Point text has no frame to justify inside — every line
 * lays out around this one point — so a left-edge origin jumps a centered layer half its width.
 */
function pointAnchorX(layer: TextLayer, justification: Justification): number {
  if (justification === "center") return layer.width / 2;
  if (justification === "right") return layer.width;
  return 0;
}

/** With an anchor set, `x`/`y` is the anchor coordinate, not the visual top-left. */
function anchorAdjustedX(layer: TextLayer): number {
  if (layer.anchorX === "center") return layer.x - layer.width / 2;
  if (layer.anchorX === "right") return layer.x - layer.width;
  return layer.x;
}

function anchorAdjustedY(layer: TextLayer): number {
  if (layer.anchorY === "middle") return layer.y - layer.height / 2;
  if (layer.anchorY === "bottom") return layer.y - layer.height;
  return layer.y;
}

/**
 * PSD font name for one weight/slant, plus whether Photoshop must synthesize. Faux bold comes off
 * when the name already carries the weight — synthesizing on top of a real Bold cut widens glyphs
 * ~6% and rewrites the design's wraps.
 */
function psdFontFor(args: {
  fontFamily: string;
  fontWeight: string;
  italic: boolean;
  resolveFont: PsdFontResolver | undefined;
}): { name: string; fauxBold: boolean; fauxItalic: boolean } {
  const { fontFamily, fontWeight, italic, resolveFont } = args;
  const resolved = resolveFont?.({ fontFamily, fontWeight, italic });
  if (!resolved) {
    // The raw value is the fallback: a PSD carries whatever name the CSS named, not "Inter".
    const name = parseFontFamilyName(fontFamily, fontFamily);
    return {
      name,
      fauxBold: isFauxBold(fontWeight) && !denotesBoldFace(name),
      fauxItalic: italic && !denotesItalicFace(name),
    };
  }
  return {
    name: resolved.name,
    fauxBold: isFauxBold(fontWeight) && !resolved.isBoldFace,
    fauxItalic: italic && !resolved.isItalicFace,
  };
}

function isFauxBold(fontWeight: string): boolean {
  // biome-ignore lint/plugin/no-number-parse-int-float: Serialized font weights retain numeric-prefix parsing semantics.
  const numeric = Number.parseInt(fontWeight, 10);
  if (Number.isFinite(numeric)) return numeric >= FAUX_BOLD_MIN_WEIGHT;
  return fontWeight === "bold" || fontWeight === "bolder";
}

/**
 * PSD tracking is thousandths of an em. Rounded because a decimal in the integer-typed `/Tracking`
 * makes Photoshop mis-parse the entire style sheet.
 */
export function trackingFromLetterSpacing(
  letterSpacing: string,
  fontSize: number,
): number | null {
  if (fontSize <= 0) return null;
  const px = parseCssLength(letterSpacing, { em: fontSize });
  if (px === null) return null;
  return Math.round((px / fontSize) * 1000);
}

/** Unitless line-height × fontSize, or a px/em value; anything else → auto. */
export function leadingFromLineHeight(
  lineHeight: string,
  fontSize: number,
): number | null {
  if (lineHeight.trim() === "normal") return null;
  return parseCssLength(lineHeight, { em: fontSize, unitless: fontSize });
}

export function textLayerTransform(
  layer: TextLayer,
  parentTransform: Affine,
): Affine {
  return multiply(
    parentTransform,
    boxTransform({
      left: anchorAdjustedX(layer),
      top: anchorAdjustedY(layer),
      width: layer.width,
      height: layer.height,
      rotation: layer.rotation,
      scale: layerScale(layer),
      flipX: layer.flipX,
      flipY: layer.flipY,
      origin: resolveTransformOrigin({
        extraStyles: layer.extraStyles,
        width: layer.width,
        height: layer.height,
        fontSize: layer.fontSize,
      }),
    }),
  );
}
